import { useEffect, useState } from 'react'

import { asClock } from '../../api/exams'

/**
 * How long somebody has been sitting at this test.
 *
 * **It counts on from what the service said, and never adds moments up itself.** The answer carries
 * the length as of that moment; this ticks a second at a time on top of it. A browser whose clock is
 * ten minutes out therefore shows the right *length* — working the time out from two timestamps would
 * show the wrong one, and nothing on the screen would say why.
 *
 * While the test is paused the number stands, which is the whole of what the pause means here. The
 * value is read out as a whole for a screen reader rather than ticked into one, because a live region
 * that announced every second would make the screen unusable.
 */
export interface ClockProps {
  /** How long it had been, in milliseconds, at the moment the service answered. */
  readonly elapsedMs: number
  /** Whether it is still counting. */
  readonly running: boolean
}

export function Clock({ elapsedMs, running }: ClockProps) {
  const [since, setSince] = useState(0)

  /*
    Only the interval lives here. Whatever the service last said is where the counting starts from,
    and that reset is the caller's: it draws this with a key made of the value, so a new answer is a
    new clock rather than an old one being written into.
  */
  useEffect(() => {
    if (!running) {
      return
    }
    const started = Date.now()
    const tick = setInterval(() => {
      setSince(Date.now() - started)
    }, 1000)
    return () => {
      clearInterval(tick)
    }
  }, [running])

  const shown = asClock(elapsedMs + since)

  return (
    <p className="clock" data-running={running ? 'true' : 'false'}>
      <span className="clock__time">{shown}</span>
      <span className="clock__what">{running ? 'at this test' : 'paused'}</span>
    </p>
  )
}
